var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { Bridge } from "@play-co/native-game-js";
import { Amplitude } from "@play-co/native-game-js/modules/Amplitude";
import { AppleSearchAds } from "@play-co/native-game-js/modules/AppleSearchAds";
import { AppsFlyer } from "@play-co/native-game-js/modules/AppsFlyer";
import { LaunchScreen } from "@play-co/native-game-js/modules/LaunchScreen";
import { LoginScreen } from "@play-co/native-game-js/modules/LoginScreen";
import { Notifications } from "@play-co/native-game-js/modules/Notifications";
import { Purchases } from "@play-co/native-game-js/modules/Purchases";
import { StoreReview } from "@play-co/native-game-js/modules/StoreReview";
import { analytics } from "../../analytics/index.mjs";
import { createError } from "../../errorUtils.mjs";
import { getAppleSearchAdsAttribution, getAppsFlyerAttribution } from "../attribution.mjs";
import { trackNotificationRegistration, trackNotificationRegistrationSuccess, trackNotificationRegistrationFailure, trackStoreReviewRequest } from "../events.mjs";
function assertAvailable(module) {
  if (!module) {
    throw new Error("API not available");
  }
}
class InstantGameInterface {
  constructor(bridge, inject) {
    __publicField(this, "interface", "instant-game");
    __publicField(this, "bridge");
    __publicField(this, "platform");
    __publicField(this, "app");
    __publicField(this, "amplitude");
    __publicField(this, "attribution");
    __publicField(this, "game");
    __publicField(this, "links");
    __publicField(this, "auth");
    __publicField(this, "notifications");
    __publicField(this, "purchases");
    __publicField(this, "store");
    bridge ?? (bridge = Bridge.getInstance());
    this.bridge = bridge;
    const appleSearchAds = (inject == null ? void 0 : inject.appleSearchAds) ?? AppleSearchAds.getInstance();
    const appsFlyer = (inject == null ? void 0 : inject.appsFlyer) ?? AppsFlyer.getInstance();
    const launchScreen = (inject == null ? void 0 : inject.launchScreen) ?? LaunchScreen.getInstance();
    const loginScreen = (inject == null ? void 0 : inject.loginScreen) ?? LoginScreen.getInstance();
    const notifications = (inject == null ? void 0 : inject.notifications) ?? Notifications.getInstance();
    const purchases = (inject == null ? void 0 : inject.purchases) ?? Purchases.getInstance();
    const storeReview = (inject == null ? void 0 : inject.storeReview) ?? StoreReview.getInstance();
    const self = this;
    this.platform = {
      get os() {
        return self.bridge.platform.os;
      },
      get version() {
        return self.bridge.platform.version;
      },
      get locale() {
        return self.bridge.platform.locale;
      }
    };
    this.app = {
      get version() {
        return self.bridge.app.version;
      }
    };
    this.amplitude = {
      getDeviceId: async () => {
        var _a;
        const deviceId = await ((_a = Amplitude.getInstance()) == null ? void 0 : _a.getDeviceId());
        return deviceId || void 0;
      }
    };
    this.attribution = (async () => {
      const appleSearchAdsAttribution = appleSearchAds ? getAppleSearchAdsAttribution(appleSearchAds) : void 0;
      const appsFlyerAttribution = appsFlyer ? getAppsFlyerAttribution(appsFlyer) : void 0;
      const combinedAttribution = {
        ...await appleSearchAdsAttribution,
        ...await appsFlyerAttribution
      };
      if (Object.keys(combinedAttribution).length === 0) {
        return void 0;
      }
      return combinedAttribution;
    })();
    this.game = {
      show: () => {
        void (launchScreen == null ? void 0 : launchScreen.hide());
      },
      reload: (_loginWithLine) => {
        const url = new URL(window.location.href);
        url.searchParams.set("cache-buster", Date.now().toString());
        this.auth.logout().catch(() => {
        }).finally(() => window.location.assign(url));
      }
    };
    this.links = /* @__PURE__ */ (() => {
      return {
        isAvailable: false,
        async firstOpened() {
          return void 0;
        },
        async list() {
          return [];
        },
        async remove(url) {
          return;
        },
        subscribe: (callback) => {
          return;
        }
      };
    })();
    this.auth = {
      isAvailable: !!loginScreen,
      fetch: async () => {
        assertAvailable(loginScreen);
        const result = await loginScreen.fetch();
        switch (result.type) {
          case "line":
            return {
              userId: result.userID,
              accessToken: result.accessToken,
              profile: result.profile
            };
          default:
            throw new Error(`The user is not authenticated with LINE`);
        }
      },
      logout: async () => {
        assertAvailable(loginScreen);
        await loginScreen.logout();
      }
    };
    this.notifications = /* @__PURE__ */ (() => {
      return {
        isAvailable: !!notifications,
        firstOpened: async () => {
          return void 0;
        },
        register: async (payload) => {
          assertAvailable(notifications);
          const isRegistered = await notifications.isRegistered();
          if (!isRegistered) {
            trackNotificationRegistration(payload);
          }
          const result = await notifications.register();
          analytics.setUserProperties({
            isRegisteredForPushNotifications: result.type === "success"
          });
          if (!isRegistered) {
            if (result.type === "success") {
              trackNotificationRegistrationSuccess(payload);
            } else {
              trackNotificationRegistrationFailure(
                createError("USER_INPUT", "Did not register for notifications"),
                payload
              );
            }
          }
          return {
            success: result.type === "success",
            deviceToken: result.type === "success" ? result.deviceToken : void 0
          };
        },
        list: async () => {
          return [];
        },
        remove: async (id) => {
          return;
        },
        subscribe: (callback) => {
          return;
        }
      };
    })();
    this.purchases = /* @__PURE__ */ (() => {
      let stashedProductIds = [];
      return {
        isAvailable: !!purchases,
        init: async (productIds) => {
          assertAvailable(purchases);
          stashedProductIds = productIds;
        },
        products: async () => {
          assertAvailable(purchases);
          const products = await purchases.products({
            ids: stashedProductIds
          });
          const legacyProducts = products.map((product) => ({
            title: product.displayName,
            id: product.id,
            description: product.description,
            imageURI: "",
            price: product.price,
            currencyCode: product.currencyCode
          }));
          return legacyProducts;
        },
        purchase: async (productId, payload) => {
          assertAvailable(purchases);
          const result = await purchases.purchase({
            id: productId,
            payload
          });
          switch (result.type) {
            case "user cancelled":
              throw createError(
                "USER_INPUT",
                "The user cancelled the purchase"
              );
            case "not found":
              throw new Error(`The product ${productId} was not found`);
            case "unknown":
              throw new Error(`An unknown (unexpected) error occurred`);
            case "unverified":
              throw new Error(`The purchase was not verified`);
            case "verified": {
              const { transaction, receipt } = result;
              const legacyPurchase = {
                paymentID: transaction.id,
                productID: transaction.productID,
                purchaseTime: transaction.purchaseDate,
                purchaseToken: transaction.id,
                signedRequest: receipt
              };
              return legacyPurchase;
            }
          }
        },
        finish: async (purchase) => {
          assertAvailable(purchases);
          const result = await purchases.finish({
            id: purchase.purchaseToken
          });
          if (result.type === "failure") {
            throw new Error(
              `Could not finish purchase ${purchase.purchaseToken}`
            );
          }
        },
        unfinished: async () => {
          assertAvailable(purchases);
          const results = await purchases.unfinished();
          const legacyPurchases = results.flatMap((result) => result.type === "verified" ? [result] : []).map(({ transaction, receipt }) => ({
            paymentID: transaction.id,
            productID: transaction.productID,
            purchaseTime: transaction.purchaseDate,
            purchaseToken: transaction.id,
            signedRequest: receipt
          }));
          return legacyPurchases;
        }
      };
    })();
    this.store = {
      isAvailable: !!storeReview,
      requestReview: async (payload) => {
        assertAvailable(storeReview);
        trackStoreReviewRequest(payload);
        await storeReview.requestReview();
      }
    };
  }
  static create(bridge = Bridge.getInstance()) {
    if (bridge) {
      switch (bridge.platform.os) {
        case "ios":
          analytics.setUserProperties({
            lastEntryAppleVersion: bridge.app.version
          });
          break;
      }
      return new InstantGameInterface(bridge);
    }
    return void 0;
  }
}
export {
  InstantGameInterface
};
